/**
 * Build a set of typed keys (`task:42`, `habit:99`, ...) for every
 * yanamas-authored calendar block, so the "To Schedule" list in
 * /review-schedule can cleanly exclude already-scheduled items.
 *
 * Habit occurrence blocks often leave `parent_task_id` null and instead
 * carry their origin on `source_item_id` / `recurring_source_id`. That
 * is the root cause of already-scheduled habits leaking back into the
 * "To Schedule" list -- the prior filter only checked `parent_task_id`.
 *
 * Rules:
 *  - External calendar events (google_calendar etc.) contribute no keys
 *    -- those aren't managed by yanamas and shouldn't shadow items in
 *    the picker.
 *  - Habit occurrences (`task_kind === 'habit_occurrence'`) produce
 *    `habit:{source_item_id || recurring_source_id}`.
 *  - Any other yanamas block with a `parent_task_id` produces
 *    `task:{parent_task_id}`.
 *
 * Keys are typed so that task id N and habit id N cannot collide.
 */

export interface ScheduledEventLike {
  source?: string;
  external_system?: string;
  task_kind?: string;
  parent_task_id?: number | string;
  source_item_id?: number | string;
  source_item_type?: string;
  recurring_source_id?: number | string;
}

export function buildScheduledItemKeys(
  events: readonly ScheduledEventLike[],
): Set<string> {
  const keys = new Set<string>();
  for (const e of events) {
    if (e.external_system) continue;
    if (e.source !== "horton") continue;

    if (e.task_kind === "habit_occurrence") {
      const habitId = e.source_item_id ?? e.recurring_source_id;
      if (habitId != null && habitId !== "") {
        keys.add(`habit:${habitId}`);
      }
      continue;
    }

    if (
      e.source_item_type &&
      e.source_item_id != null &&
      e.source_item_id !== ""
    ) {
      // Future-proofing for explicit source_item_type on non-habit blocks.
      keys.add(`${e.source_item_type}:${e.source_item_id}`);
      continue;
    }

    if (e.parent_task_id != null && e.parent_task_id !== "") {
      keys.add(`task:${e.parent_task_id}`);
    }
  }
  return keys;
}
